import { c as _c } from "react/compiler-runtime";
import { type KeyboardEvent, useEffect, useMemo, useRef } from 'react';
import { SearchInput, type SearchInputProps } from '@/app/components/forms/search-input';
import { Icon } from '@/app/components/icons';
import { IconLocationOn } from '@/app/components/icons/maps/icon-location-on';
import { IconChevronRight } from '@/app/components/icons/ui-actions/icon-chevron-right';
import { DealerEmptyState } from '@/app/components/sections/dealer/components/shared/empty-state';
import { InfiniteScrollList } from '@/app/components/sections/dealer/components/shared/infinite-scroll-list';
import { useDealerSearchKey } from '@/app/components/sections/dealer/hooks/useDealerSearchKey';
import styles from '@/app/components/sections/dealer/styles.module.css';
import { DEALER_DEFAULT_VALUE, DEALER_EMPTY_STATE_DATA, DEALER_TYPES, THAILAND_LOCATIONS } from '@/app/components/sections/dealer/utils/dealer-constant';
import { ICON_DEALER_TYPE_MAP } from '@/app/components/sections/dealer/utils/dealer-icons';
import { searchSuggestPlaces } from '@/app/components/sections/dealer/utils/dealer-search';
import { createDealerNameFormat } from '@/app/components/sections/dealer/utils/format';
import type { DealerPlace } from '@/app/components/sections/dealer/utils/types';
import { DEALER_PLACEHOLDER_SEARCH } from '@/app/constants/dealer';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';
import { type DealerGrouping } from '@/services/contentstack/models/dealer';
const MAX_LENGTH = 3;
const GROUP_NAME = 'components/shared/dealer/shared/smart-search/search-box';
export interface SearchBoxProps extends Omit<SearchInputProps, 'name' | 'value'> {
  variant?: 'default' | 'list';
  searchWrapperClassName?: string;
  name?: string;
  items?: DealerGrouping[];
  fullScreenEmptyState?: boolean;
  selfClose?: boolean;
  hideAutoComplete?: boolean;
  value: string;
  onClose?: () => void;
  onEnter?: (value: string) => void;
  onChangeSearch?: (value: string, isFromParams: boolean) => void;
  onClickSuggestPlace?: (value: DealerPlace) => void;
  onClickSearchResult?: (value: DealerGrouping, type: string) => void;
  onClear?: () => void;
}
interface SuggestPlaceProps {
  name: string;
  onClick: () => void;
}
interface ResultPlaceProps {
  name: string;
  address: string;
  icon: string;
  onClick: () => void;
}
const baseClassName = 'hover:bg-silver-100 flex cursor-pointer items-center gap-x-2 px-4';
const SuggestPlace = t0 => {
  const $ = _c(13);
  let name;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      name,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = name;
    $[2] = onClick;
    $[3] = props;
  } else {
    name = $[1];
    onClick = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(baseClassName, "w-full py-2");
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon className="size-5 text-red-500" icon={<IconLocationOn />} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = name ?? "";
  const t4 = name ?? "";
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <span title={t3} className="text-body-1-fixed overflow-hidden text-ellipsis whitespace-nowrap text-gray-700">{t4}</span>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== onClick || $[10] !== props || $[11] !== t5) {
    t6 = <li className={t1} onClick={onClick} {...props}>{t2}{t5}</li>;
    $[9] = onClick;
    $[10] = props;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
const ResultPlace = t0 => {
  const $ = _c(24);
  let address;
  let icon;
  let name;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      name,
      address,
      onClick,
      icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = address;
    $[2] = icon;
    $[3] = name;
    $[4] = onClick;
    $[5] = props;
  } else {
    address = $[1];
    icon = $[2];
    name = $[3];
    onClick = $[4];
    props = $[5];
  }
  const dealerType = DEALER_TYPES.find(item => item.id === icon);
  const renderIcon = dealerType?.icon || "IconStore";
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(baseClassName, "h-fit py-3");
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = ICON_DEALER_TYPE_MAP[renderIcon];
  let t3;
  if ($[7] !== t2) {
    t3 = <Icon className="mr-2 size-5 text-red-500" icon={t2} />;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== name) {
    t4 = createDealerNameFormat(name);
    $[9] = name;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <p className="text-body-2-fixed font-noto-bold where-span:text-inline-wrap" dangerouslySetInnerHTML={{
      __html: t4
    }} />;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = address || DEALER_DEFAULT_VALUE.ADDRESS;
  let t7;
  if ($[13] !== t6) {
    t7 = <span className="text-caption line-clamp-1 text-gray-600">{t6}</span>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div>{t5}{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Icon className="ml-auto text-gray-800" icon={<IconChevronRight />} />;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== onClick || $[20] !== props || $[21] !== t3 || $[22] !== t8) {
    t10 = <li className={t1} onClick={onClick} {...props}>{t3}{t8}{t9}</li>;
    $[19] = onClick;
    $[20] = props;
    $[21] = t3;
    $[22] = t8;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
export const SearchBox = t0 => {
  const $ = _c(65);
  let onChangeSearch;
  let onClear;
  let onClickSearchResult;
  let onClickSuggestPlace;
  let onClose;
  let onEnter;
  let props;
  let searchWrapperClassName;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let value;
  if ($[0] !== t0) {
    ({
      variant: t1,
      searchWrapperClassName,
      name: t2,
      value,
      items: t3,
      fullScreenEmptyState: t4,
      hideAutoComplete: t5,
      onClickSuggestPlace,
      onClickSearchResult,
      onChangeSearch,
      onClose,
      onEnter,
      selfClose: t6,
      onClear,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onChangeSearch;
    $[2] = onClear;
    $[3] = onClickSearchResult;
    $[4] = onClickSuggestPlace;
    $[5] = onClose;
    $[6] = onEnter;
    $[7] = props;
    $[8] = searchWrapperClassName;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = value;
  } else {
    onChangeSearch = $[1];
    onClear = $[2];
    onClickSearchResult = $[3];
    onClickSuggestPlace = $[4];
    onClose = $[5];
    onEnter = $[6];
    props = $[7];
    searchWrapperClassName = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    value = $[15];
  }
  const variant = t1 === undefined ? "default" : t1;
  const name = t2 === undefined ? "search-dealer" : t2;
  let t7;
  if ($[16] !== t3) {
    t7 = t3 === undefined ? [] : t3;
    $[16] = t3;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const items = t7;
  const fullScreenEmptyState = t4 === undefined ? false : t4;
  const hideAutoComplete = t5 === undefined ? false : t5;
  const selfClose = t6 === undefined ? false : t6;
  const {
    removeTextSearchParams
  } = useDealerSearchKey();
  const inputRef = useRef(null);
  let t8;
  let t9;
  if ($[18] !== variant) {
    t8 = () => {
      if (variant !== "default") {
        return;
      }
      const requestId = requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      });
      return () => cancelAnimationFrame(requestId);
    };
    t9 = [variant];
    $[18] = variant;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  if ($[21] !== onChangeSearch) {
    t10 = (value_0, t11) => {
      const isFromParams = t11 === undefined ? false : t11;
      const allWhiteSpaces = value_0.length > 0 && value_0.trim().length === 0;
      if (allWhiteSpaces) {
        return;
      }
      safeCall(onChangeSearch, value_0, isFromParams);
    };
    $[21] = onChangeSearch;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const handleChange = t10;
  const isNotAcceptSearch = !value || value.trim().length < MAX_LENGTH;
  let t11;
  bb0: {
    if (items?.length === 0 || isNotAcceptSearch) {
      let t12;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = [];
        $[23] = t12;
      } else {
        t12 = $[23];
      }
      t11 = t12;
      break bb0;
    }
    t11 = items;
  }
  const searchData = t11;
  let t12;
  bb1: {
    if (isNotAcceptSearch) {
      let t13;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = [];
        $[24] = t13;
      } else {
        t13 = $[24];
      }
      t12 = t13;
      break bb1;
    }
    let t13;
    if ($[25] !== value) {
      t13 = searchSuggestPlaces(THAILAND_LOCATIONS, value) || [];
      $[25] = value;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    t12 = t13;
  }
  const suggestPlaces = t12;
  let t13;
  if ($[27] !== onClickSuggestPlace || $[28] !== onClose) {
    t13 = item => {
      safeCall(onClose);
      safeCall(onClickSuggestPlace, item);
    };
    $[27] = onClickSuggestPlace;
    $[28] = onClose;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const handleSuggestPlaceClick = t13;
  let t14;
  if ($[30] !== onClickSearchResult || $[31] !== onClose) {
    t14 = (item_0, type) => {
      safeCall(onClose);
      safeCall(onClickSearchResult, item_0, type);
    };
    $[30] = onClickSearchResult;
    $[31] = onClose;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const handleSearchResultClick = t14;
  let t15;
  if ($[33] !== onClose || $[34] !== onEnter || $[35] !== selfClose || $[36] !== value) {
    t15 = event => {
      if (value.length < MAX_LENGTH || event.key !== "Enter") {
        return;
      }
      safeCall(onEnter, value);
      if (selfClose) {
        return;
      }
      safeCall(onClose);
    };
    $[33] = onClose;
    $[34] = onEnter;
    $[35] = selfClose;
    $[36] = value;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const handleEnter = t15;
  let t16;
  if ($[38] !== onClear || $[39] !== removeTextSearchParams) {
    t16 = () => {
      removeTextSearchParams("", false);
      safeCall(onClear);
    };
    $[38] = onClear;
    $[39] = removeTextSearchParams;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const handleClear = t16;
  const showSuggestPlaces = !!value && suggestPlaces.length > 0;
  const listClassName = cn("flex-col space-y-2", styles.xlSearchResultPanel);
  const defaultClassName = cn("flex-col xl:flex-row xl:justify-between xl:gap-x-4 xl:px-10 xl:py-4", styles.searchResultsContainer);
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = createDataAttributes({
      test: "search-input"
    });
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== handleChange || $[43] !== handleClear || $[44] !== handleEnter || $[45] !== name || $[46] !== props || $[47] !== value) {
    t18 = <SearchInput {...props} inputRef={inputRef} placeholder={DEALER_PLACEHOLDER_SEARCH} inputClassName="w-full" name={name} onChange={handleChange} value={value} maxLength={50} onKeyDown={handleEnter} onClear={handleClear} {...t17} />;
    $[42] = handleChange;
    $[43] = handleClear;
    $[44] = handleEnter;
    $[45] = name;
    $[46] = props;
    $[47] = value;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== searchWrapperClassName || $[50] !== t18) {
    t19 = <div className={searchWrapperClassName}>{t18}</div>;
    $[49] = searchWrapperClassName;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== fullScreenEmptyState || $[53] !== handleSearchResultClick || $[54] !== handleSuggestPlaceClick || $[55] !== hideAutoComplete || $[56] !== searchData || $[57] !== showSuggestPlaces || $[58] !== suggestPlaces || $[59] !== value.length || $[60] !== variant) {
    t20 = !hideAutoComplete && <>{searchData?.length > 0 || suggestPlaces.length > 0 ? <div className={cn("flex h-full", {
        [listClassName]: variant === "list",
        [defaultClassName]: variant === "default",
        "pb-10 xl:pb-0": variant === "default" && suggestPlaces.length > 0
      })}>{!!showSuggestPlaces && <ul className={cn({
          "w-full": searchData.length === 0
        })}>{[...suggestPlaces].slice(0, 3).map((item_1, index) => <SuggestPlace key={index} name={item_1.name} onClick={() => handleSuggestPlaceClick(item_1)} {...createDataAttributes({
            test: `suggest-place-${index}`
          })} {...createDataGA({
            click: true,
            eventName: "dealer-search",
            group: GROUP_NAME,
            value: `${item_1.lat},${item_1.lng}`,
            text: item_1.name
          })} />)}</ul>}{searchData.length > 0 && <div className="flex h-full min-h-0 w-full flex-col"><p className="text-body-2-fixed font-noto-bold shrink-0 px-4 py-1" {...createDataAttributes({
            test: "title-search-result"
          })}>ผลการค้นหา {`(${searchData.length})`}</p><ul className="relative min-h-0 flex-1 overflow-y-auto"><InfiniteScrollList items={searchData} renderItem={(item_2, index_0) => <ResultPlace key={item_2?.groupForSearch?.id || index_0} name={`${item_2?.groupForSearch?.name ?? ""}`} address={item_2?.groupForSearch?.address ?? ""} icon={item_2?.groupForSearch?.type ?? ""} onClick={() => handleSearchResultClick(item_2, item_2.groupForSearch?.type || "")} {...createDataAttributes({
              test: `result-place-${index_0}`
            })} {...createDataGA({
              click: true,
              eventName: "dealer-search",
              group: GROUP_NAME,
              value: item_2.uid,
              text: item_2?.groupForSearch?.name
            })} />} /></ul></div>}</div> : value.length >= MAX_LENGTH && <>{variant === "list" && <p className="text-body-2-fixed px-4 py-3" {...createDataAttributes({
          test: "title-search-result"
        })}>ผลการค้นหา {`(${searchData.length})`}</p>}<DealerEmptyState className={cn({
          "mx-4 mb-7": !fullScreenEmptyState,
          "absolute top-24 left-0 z-50 mt-5 w-full rounded-none": fullScreenEmptyState
        })} title={variant === "list" ? DEALER_EMPTY_STATE_DATA.TITLE_BY_SEARCH : ""} fullScreen={variant === "list"} /></>}</>;
    $[52] = fullScreenEmptyState;
    $[53] = handleSearchResultClick;
    $[54] = handleSuggestPlaceClick;
    $[55] = hideAutoComplete;
    $[56] = searchData;
    $[57] = showSuggestPlaces;
    $[58] = suggestPlaces;
    $[59] = value.length;
    $[60] = variant;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== t19 || $[63] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[62] = t19;
    $[63] = t20;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  return t21;
};